import { DatePicker, Form, Space, Typography } from 'antd'
import { FormProps, useForm, useWatch } from 'antd/lib/form/Form'
import { InspectionFormValues } from './types'
import { useProjectSettingsContext } from '../../../../../../ProjectSettingsContext'
import { useEffect, useState } from 'react'
import { toMoment } from 'utils/toMoment'
import CIMSFormItem from 'components/CIMSFormItem'
import { HelpMessage } from 'utils/constants'
import CIMSSwitch from 'components/CIMSSwitch'
import CIMSCollapse from 'components/CIMSCollapse'
import DirtyHeader from '../../../../components/DirtyHeader'
import { IFIReportType, notEqual } from '@cims-online/shared'
import CIMSTextArea from 'components/CIMSTextArea'
import { DisabledOptions } from 'hooks/useDisabledOptions'
import { CommonEmbeddedEditPanelProps } from '../../../common'
import { useUpsertInspectionIfiSettingsMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { ifiSettingsEditNotification, momentNotEqual } from '../../../../utils'
import { InspectionPanelName } from '../../types'
import CIMSPrompt from 'components/CIMSPrompt'
import EditPanelFooter from '../../../../components/EditPanelFooter'
import { useProjectContext } from 'context/ProjectContext'

const Help = HelpMessage.Administration.ProjectSettings.IFITracker
const {
  id: reportId,
  type: reportType,
  defaultMessages: { missing },
} = IFIReportType.DailyInspectionInspection

const InspectionEditPanel: React.VFC<
  CommonEmbeddedEditPanelProps<InspectionPanelName>
> = ({ dirtyPanels, setDirtyPanels, openPanels, setOpenPanels }) => {
  const { projectId } = useProjectContext()
  const {
    inspectionIFISettings: { data, refetch },
  } = useProjectSettingsContext()
  const [upsert, { loading }] = useUpsertInspectionIfiSettingsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => ifiSettingsEditNotification(reportType),
  })
  const [initialValues, setInitialValues] = useState<InspectionFormValues>({})
  const [form] = useForm<InspectionFormValues>()

  const handleOnValuesChange: FormProps<InspectionFormValues>['onValuesChange'] =
    (_, currentValues) => {
      const newDirtyPanels: InspectionPanelName[] = []
      if (
        notEqual(initialValues.enabled, currentValues.enabled) ||
        momentNotEqual(initialValues.effectiveDate, currentValues.effectiveDate)
      )
        newDirtyPanels.push('parent')
      if (
        notEqual(
          initialValues.missingGeoEnabled,
          currentValues.missingGeoEnabled
        ) ||
        notEqual(initialValues.message, currentValues.message)
      )
        newDirtyPanels.push('missing')

      setDirtyPanels(newDirtyPanels)
    }

  const enabledWatch = useWatch('enabled', form)
  const enabledDisabledOptions: DisabledOptions = {
    disabled: !enabledWatch,
    reason: {
      why: 'disabled',
      message: `Cannot edit while ${reportType} is disabled`,
    },
  }

  useEffect(() => {
    if (!enabledWatch) {
      form.setFieldsValue({
        missingGeoEnabled: false,
        message: initialValues.message,
      })
      handleOnValuesChange({}, form.getFieldsValue())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledWatch])

  useEffect(() => {
    form.setFieldsValue(initialValues)
    handleOnValuesChange({}, form.getFieldsValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  useEffect(() => {
    setInitialValues({
      enabled: data?.enabled,
      effectiveDate: toMoment(data?.effectiveDate),
      missingGeoEnabled: data?.missingGeometry?.enabled,
      message: data?.missingGeometry?.message,
    })
  }, [data])

  return (
    <CIMSPrompt when={dirtyPanels.length > 0}>
      <Form
        form={form}
        initialValues={initialValues}
        onValuesChange={handleOnValuesChange}
        onFinish={async ({
          enabled,
          effectiveDate,
          missingGeoEnabled,
          message,
        }) => {
          await upsert({
            variables: {
              input: {
                projectId,
                enabled,
                effectiveDate:
                  effectiveDate === null ? null : effectiveDate?.toISOString(),
                missingGeometry: {
                  enabled: missingGeoEnabled,
                  message,
                },
              },
            },
          })
          refetch()
        }}
      >
        <Typography>
          If Unresolved Issues Associated with this Work is set to Yes, issue an
          Identified Field Issue using the NCR Issue Description.
        </Typography>
        <Space size="large" style={{ marginTop: '15px' }}>
          <CIMSFormItem
            label="Enabled"
            name="enabled"
            help={Help.Enabled}
            valuePropName="checked"
          >
            <CIMSSwitch onClick={(_, event) => event.stopPropagation()} />
          </CIMSFormItem>
          <CIMSFormItem
            label="Effective Date"
            name="effectiveDate"
            help={Help.EffectiveDate}
            disabled={enabledDisabledOptions}
          >
            <DatePicker disabled={!enabledWatch} />
          </CIMSFormItem>
        </Space>
        <CIMSCollapse
          multiple
          activeKey={openPanels}
          onChange={setOpenPanels}
          panels={[
            {
              header: {
                children: (
                  <DirtyHeader
                    title="Missing Geometry"
                    isDirty={dirtyPanels.includes('missing')}
                  />
                ),
              },
              key: `edit-missing-geometry-${reportId}`,
              forceRender: true,
              children: (
                <Space direction="vertical" size="middle">
                  <Typography>
                    If the Inspection is missing geometry and its Review Status
                    is "Submitted", "Approved", or "Denied", issue an Identified
                    Field Issue using the Message. This may issue an Identified
                    Field Issue for an Inspection without Unresolved Issues.
                  </Typography>
                  <CIMSFormItem
                    name="message"
                    label="Message"
                    help={Help.FailureMessage()}
                    disabled={enabledDisabledOptions}
                    defaultMessage={missing}
                  >
                    <CIMSTextArea disabled={!enabledWatch} />
                  </CIMSFormItem>
                </Space>
              ),
              extra: {
                children: (
                  <CIMSFormItem
                    noStyle
                    name="missingGeoEnabled"
                    valuePropName="checked"
                  >
                    <CIMSSwitch
                      onClick={(_, event) => event.stopPropagation()}
                      disabled={enabledDisabledOptions}
                    />
                  </CIMSFormItem>
                ),
              },
            },
          ]}
        />
        <EditPanelFooter
          {...{
            form,
            loading,
            handleOnValuesChange,
            hasChanges: dirtyPanels.length > 0,
          }}
        />
      </Form>
    </CIMSPrompt>
  )
}

export default InspectionEditPanel
